import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import ProductCard from "./ProductCard";
import Cart, { generateCartItemsFrom } from "./Cart";
import { useSnackbar } from "notistack";

import "./Products.css";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const debounceTimeout = useRef(null);

  // const isItemInCart = (items, productId) => {
  //   return items.some((item) => item.productId === productId);
  // };

  const addToCart = async (productId, qty) => {
    const token = localStorage.getItem("token");

    if (!token) {
      enqueueSnackbar("Login to add an item to the Cart", {
        variant: "warning",
      });
      return;
    }

    // 🚨 Only block if ADD TO CART button clicked
    const existingItem = cartItems.find((item) => item.productId === productId);

    if (existingItem && qty === 1) {
      enqueueSnackbar(
        "Item already in cart. Use the cart sidebar to update quantity or remove item.",
        { variant: "warning" }
      );
      return;
    }

    try {
      const res = await axios.post(
        `${config.endpoint}/cart`,
        { productId, qty },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updated = generateCartItemsFrom(res.data, products);
      setCartItems(updated);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    setIsSearching(false);
    try {
      const res = await axios.get(`${config.endpoint}/products`);
      setProducts(res.data);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async (token, productsData) => {
    try {
      const res = await axios.get(`${config.endpoint}/cart`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const merged = generateCartItemsFrom(res.data, productsData);
      setCartItems(merged);
    } catch (e) {
      console.log("Error fetching cart");
    }
  };

  // -----------------------------
  // Search API
  // -----------------------------
  const performSearch = async (text) => {
    setLoading(true);
    setIsSearching(true);

    try {
      const res = await axios.get(
        `${config.endpoint}/products/search?value=${text}`
      );
      setProducts(res.data);
    } catch (e) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Debounced search handler
  // -----------------------------
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (value.trim() === "") {
        fetchProducts();
      } else {
        performSearch(value);
      }
    }, 500);
  };

  // useEffect(() => {
  //   fetchProducts();
  // }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      try {
        const res = await axios.get(`${config.endpoint}/products`);
        setProducts(res.data);

        const token = localStorage.getItem("token");
        if (token) {
          await fetchCart(token, res.data);
        }
      } catch (e) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const searchView = (
    <Box className="search">
      <TextField
        fullWidth
        size="small"
        placeholder="Search for items/categories"
        value={searchText}
        onChange={handleSearch}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );

  return (
    <div>
      <Header>{searchView}</Header>
  
      <Box className="search-mobile">{searchView}</Box>
  
      <Grid container spacing={2}>
        {/* PRODUCTS SECTION */}
        <Grid
          item
          xs={12}
          md={localStorage.getItem("token") ? 9 : 12}
        >
          <Grid container className="product-grid">
            {/* Hero */}
            <Grid item xs={12}>
              <Box className="hero">
                <p className="hero-heading">
                  India’s{" "}
                  <span className="hero-highlight">
                    FASTEST DELIVERY
                  </span>{" "}
                  to your door step
                </p>
              </Box>
            </Grid>
  
            {/* Product Content */}
            {loading ? (
              <Box className="loading">
                <CircularProgress />
                <p>Loading</p>
              </Box>
            ) : isSearching && products.length === 0 ? (
              <Box className="empty">
                <SentimentDissatisfied />
                <p>No products found</p>
              </Box>
            ) : (
              products.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  addToCart={addToCart}
                />
              ))
            )}
          </Grid>
        </Grid>
  
        {/* CART SECTION (ONLY IF LOGGED IN) */}
        {localStorage.getItem("token") && (
          <Grid item xs={12} md={3}>
            <Cart
              products={products}
              items={cartItems}
              addToCart={addToCart}
            />
          </Grid>
        )}
      </Grid>
  
      <Footer />
    </div>
  );
  
};

export default Products;
